import { useEffect, useRef, useState } from 'react';

import { notify } from '@/components/Notice/Notice';
import {
    type BrandIntelligence,
    type BrandIntelligenceParams,
    brandReportUrl,
} from '@/features/reports/api';
import {
    brandReportDocument,
    canGenerate,
    GENERATE_STATE,
    type GenerateState,
} from '@/features/reports/brands/brandReport';
import { BRANDS_COPY as COPY } from '@/features/reports/copy';
import { opensPdfInTab } from '@/features/orders/shipping/parcels';
import type { PdfDocument } from '@/features/orders/shipping/PdfViewer';
import { fetchFile, saveBlob } from '@/lib/download';

/** How long the button says Report ready before it is back at rest, as the prototype's does. */
export const READY_FOR_MS = 1800;

/**
 * Generate report (ALS-93): asks the API for the brand report PDF, says Generating… while it is
 * set and Report ready once it is, then opens it in the document viewer the invoices open in. The
 * PDF is fetched once and the viewer, its Download and its Print all read that copy, so nothing is
 * set twice.
 *
 * A phone draws no PDF inside a page, and a tab opened after the fetch would be blocked as a
 * pop-up, so there the report is saved as its file instead and the phone opens it. A failure says
 * so and puts the button back at rest, never silently. Changing the brand or the quarter remounts
 * the caller, which drops a fetch still running and lets go of the last report.
 */
export function useGenerateReport(
    report: BrandIntelligence | undefined,
    params: BrandIntelligenceParams | undefined,
) {
    const [state, setState] = useState<GenerateState>(GENERATE_STATE.Idle);
    const [document, setDocument] = useState<PdfDocument | null>(null);
    const running = useRef<AbortController | null>(null);
    const src = useRef<string | null>(null);

    useEffect(() => {
        if (state !== GENERATE_STATE.Ready) return;
        const rest = window.setTimeout(() => setState(GENERATE_STATE.Idle), READY_FOR_MS);
        return () => window.clearTimeout(rest);
    }, [state]);

    useEffect(
        () => () => {
            running.current?.abort();
            release();
        },
        [],
    );

    function release() {
        if (src.current !== null) URL.revokeObjectURL(src.current);
        src.current = null;
    }

    async function generate() {
        // A second click while the first is still setting the PDF asks for nothing more.
        if (!report || !params || !canGenerate(report) || running.current) return;
        const controller = new AbortController();
        running.current = controller;
        setState(GENERATE_STATE.Generating);
        try {
            const pdf = await fetchFile(brandReportUrl(params), controller.signal);
            if (controller.signal.aborted) return;
            const opened = brandReportDocument(report, '');
            if (opensPdfInTab()) {
                saveBlob(pdf, opened.fileName);
            } else {
                release();
                src.current = URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
                setDocument({ ...opened, url: src.current });
            }
            setState(GENERATE_STATE.Ready);
        } catch {
            if (controller.signal.aborted) return;
            notify.error(COPY.generate.failed);
            setState(GENERATE_STATE.Idle);
        } finally {
            if (running.current === controller) running.current = null;
        }
    }

    function close() {
        setDocument(null);
        release();
    }

    return { state, generate, document, close };
}
